import type { TokenSpan } from './scopeQuery';

/**
 * Rendering + caret plumbing for the ⌘K palette's editable query field.
 *
 * The query STRING stays the single source of truth (parseQuerySpans → spans). This
 * module turns that string into the DOM the user edits: recognised token spans (with
 * the value typed into them) become Kbd-styled chips, the gaps stay plain text. The
 * field is a `contentEditable` div whose children are built HERE imperatively — React
 * never owns them, so an unrelated re-render (a search settling mid-type) can never
 * reconcile the DOM back and wipe an in-flight IME composition.
 *
 * One editable layer: the caret must be native to the text carrying the chips.
 *
 *   textContent(field) === query   ALWAYS (no folded spaces, no phantom glyphs — the
 *   boundary marker below IS a real character of the query, counted like any other), so
 *   a DOM caret maps to a string offset by plain character counting.
 */

export const MONO = '[font-family:ui-monospace,SFMono-Regular,monospace]';

/**
 * The chip boundary marker: a zero-width space (U+200B). A space is chip CONTENT (`so:water melon` is ONE chip); the only escape is Right-arrow at the chip end, which inserts this marker.
 * It lives IN THE STRING so it relocates naturally under edits, stops the paint run, doubles as a word separator, and is stripped before the search term reaches Typesense (scopeQuery.stripSpans). It never renders and never reaches `q`.
 */
export const QUERY_BOUNDARY = '\u200b';

// The chip's VISUAL styling, shared verbatim with the footer/header `Kbd` component. Font size is set by each consumer, and the field variant is inline-block (not Kbd's inline-flex) so a caret can sit INSIDE the chip and type to extend it.
const GS_KBD_VISUAL = `rounded-[5px] border border-neutral-5 bg-white px-[4px] ${MONO}`;

// The footer/header Kbd: centres its 11px glyph/icon in an 18px inline-flex box.
export const GS_KBD_CLASS = `inline-flex h-[18px] min-w-[18px] items-center justify-center align-middle text-11 ${GS_KBD_VISUAL}`;

// `inline-block` + matched line box, NOT flex centring — flex centring corrupts contenteditable caret hit-testing in Safari/Firefox.
export const GS_FIELD_CHIP_CLASS = `inline-block h-[20px] leading-[20px] align-middle text-12 text-brand-6 ${GS_KBD_VISUAL}`;

export const GS_FIELD_CHIP_OVERRIDDEN_CLASS = `inline-block h-[20px] leading-[20px] align-middle text-12 text-neutral-alpha-6 line-through ${GS_KBD_VISUAL}`;

export function pillTokenEnd(query: string, span: TokenSpan, nextSpanStart: number = query.length): number {
  const marker = query.indexOf(QUERY_BOUNDARY, span.end);
  let end = Math.min(nextSpanStart, marker === -1 ? query.length : marker, query.length);
  while (end > span.end && /\s/.test(query[end - 1])) end -= 1;
  return end;
}

/** True when a collapsed caret sits exactly at a chip's paint end — the point Right escapes. */
export function isCaretAtPillEnd(query: string, spans: readonly TokenSpan[], caret: number): boolean {
  return spans.some((span, index) => pillTokenEnd(query, span, spans[index + 1]?.start) === caret);
}

/**
 * Sanitise a pasted clipboard string before it enters the field: flatten newlines/tabs
 * to a space AND strip any boundary marker, so pasted content can never inject a phantom
 * chip boundary that would corrupt the field's paint/parse.
 */
export function sanitizePastedText(text: string): string {
  return text.replace(/[\r\n\t]+/g, ' ').replace(/\u200b/g, '');
}

export interface FieldSegment {
  text: string;
  pill: boolean;
  /** Which breadth axis a pill segment's token drives — present only on pills. */
  dim?: TokenSpan['filter']['dim'];
}

/**
 * Split the query into the segments the field paints: tokens are chips, gaps are text.
 * Invariant: concatenating every segment's text yields EXACTLY the query (caret mapping).
 */
export function fieldSegments(query: string, spans: readonly TokenSpan[]): FieldSegment[] {
  const segments: FieldSegment[] = [];
  let pos = 0;
  spans.forEach((span, index) => {
    if (span.start > pos) segments.push({ text: query.slice(pos, span.start), pill: false });
    const end = pillTokenEnd(query, span, spans[index + 1]?.start);
    segments.push({ text: query.slice(span.start, end), pill: true, dim: span.filter.dim });
    pos = end;
  });
  if (pos < query.length) segments.push({ text: query.slice(pos), pill: false });
  return segments;
}

/** Build the field's child nodes. `typeOverridden` paints `type` pills muted. */
export function buildFieldNodes(query: string, spans: readonly TokenSpan[], typeOverridden = false): Node[] {
  return fieldSegments(query, spans).map((segment) => {
    if (!segment.pill) return document.createTextNode(segment.text);
    const overridden = typeOverridden && segment.dim === 'type';
    const chip = document.createElement('span');
    chip.className = overridden ? GS_FIELD_CHIP_OVERRIDDEN_CLASS : GS_FIELD_CHIP_CLASS;
    chip.setAttribute('data-gs-pill', '');
    if (overridden) {
      chip.setAttribute('data-gs-overridden', '');
      chip.setAttribute('title', 'Overridden by the filter above');
    }
    chip.textContent = segment.text;
    return chip;
  });
}

/** The collapsed caret's offset into the field's text content, or null when the selection is outside the field. Counts characters across text nodes AND chip spans alike — because textContent === query, this offset IS the string offset. */
export function getCaretOffset(field: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!field.contains(range.startContainer)) return null;
  const toCaret = range.cloneRange();
  toCaret.selectNodeContents(field);
  toCaret.setEnd(range.startContainer, range.startOffset);
  return toCaret.toString().length;
}

/** Place a collapsed caret at string `offset` by walking the field's text nodes in order. */
export function setCaretOffset(field: HTMLElement, offset: number): void {
  const selection = window.getSelection();
  if (!selection) return;
  const walker = document.createTreeWalker(field, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let target: Text | null = null;
  let local = 0;
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) {
      target = node;
      local = remaining;
      break;
    }
    remaining -= length;
  }
  const range = document.createRange();
  if (target) {
    range.setStart(target, local);
  } else {
    // Empty field or offset past the end: collapse to the very end of the field.
    range.selectNodeContents(field);
    range.collapse(false);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function renderField(
  field: HTMLElement,
  query: string,
  spans: readonly TokenSpan[],
  caretOffset: number | null,
  typeOverridden = false
): void {
  const focused = document.activeElement === field;
  const restoreTo = caretOffset ?? (focused ? getCaretOffset(field) : null);
  field.replaceChildren(...buildFieldNodes(query, spans, typeOverridden));
  if (restoreTo !== null) setCaretOffset(field, restoreTo);
}
